import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db8geerk13ra8mig8rf0";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      // Silently treat bots as a normal submission.
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试，或通过下方邮箱与我们联系。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
          <i className="ri-mail-check-line text-2xl text-accent-700" />
        </span>
        <h3 className="font-heading font-bold text-xl text-foreground-950 mb-2">提交成功</h3>
        <p className="text-sm text-foreground-500 mb-5">
          感谢你的留言，我们已经收到，会尽快通过邮箱回复你。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-800 text-sm font-medium px-5 py-2.5 rounded-md hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-edit-line" />
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="db8geerk13ra8mig8rf0"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <input
        type="text"
        name="website_alt"
        className="hp-honeypot"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <h2 className="font-heading font-black text-xl text-foreground-950 mb-1">在线留言</h2>
      <p className="text-sm text-foreground-500 mb-5">
        填写下面的表单，把你想说的话告诉我们。
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors placeholder:text-foreground-400"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors placeholder:text-foreground-400"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
            留言 <span className="text-primary-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请输入你想咨询或反馈的内容（最多 500 字）"
            className="w-full rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none placeholder:text-foreground-400"
          />
        </div>
      </div>

      {status === "error" && formError && (
        <p className="mt-4 text-sm text-primary-600 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line" />
        {status === "submitting" ? "提交中..." : "提交留言"}
      </button>
    </form>
  );
}